import React from 'react';
import { X, Zap, Check } from 'lucide-react';
import { Typography } from '../../interfaces/typography';

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function UpgradeModal({ isOpen, onClose }: UpgradeModalProps) {
  if (!isOpen) return null;

  const features = [
    'Export session data to CSV',
    'Up to 1 week of data per export',
    'All session details and metrics',
    'Direct links to transcripts',
    'Unlimited exports',
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-indigo-600" />
            <Typography variant="h3" color="#1F2937" fontWeight={600}>
              Upgrade Required
            </Typography>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-md transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-4">
          <div className="space-y-2">
            <Typography variant="mediumParagraph" color="#374151">
              Data export is available to paid Parallel users with admin or team permissions.
            </Typography>
          </div>

          {/* Features List */}
          <div className="space-y-3 py-4">
            <Typography variant="mediumParagraph" color="#1F2937" fontWeight={600}>
              What you'll get:
            </Typography>
            <div className="space-y-2">
              {features.map((feature, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-0.5">
                    <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center">
                      <Check className="w-3 h-3 text-green-600" />
                    </div>
                  </div>
                  <Typography variant="smallParagraph" color="#6B7280">
                    {feature}
                  </Typography>
                </div>
              ))}
            </div>
          </div>

          {/* Info Box */}
          <div className="px-4 py-3 bg-indigo-50 border border-indigo-200 rounded-md">
            <Typography variant="smallParagraph" color="#4F46E5">
              Contact your team admin or reach out to support to upgrade your account.
            </Typography>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
          <button
            onClick={() => {
              window.open('mailto:support@trellus.ai?subject=Upgrade Request - Data Export', '_blank');
            }}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 transition-colors"
          >
            <Zap className="w-4 h-4" />
            Contact Support
          </button>
        </div>
      </div>
    </div>
  );
}
